import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import PushSubscribeButton from '../pushSubscribeButton/PushSubscribeButton.jsx';
import { AuthContext } from '../../context/AuthContext';
import PresenceSettingsModal, {
  presenceTooltipCopy,
} from '../presence/PresenceSettingsModal';

const RESURFACE_MS = 24 * 60 * 60 * 1000;

export default function PushNudgeRail({
  product = 'connect',
  hideWhenSubscribed = false,
}) {
  const { player } = useContext(AuthContext);
  const [state, setState] = useState({
    supported: true,
    permission: 'default',
    subscribed: false,
    ready: false,
  });
  const [hydrated, setHydrated] = useState(false);
  const actionRef = useRef(null);
  const [showPresenceSettings, setShowPresenceSettings] = useState(false);
  const [forceHidden, setForceHidden] = useState(false);

  const dismissKey = useMemo(
    () => `pushNudgeRail:dismissedAt:${product}`,
    [product],
  );

  const [dismissedAt, setDismissedAt] = useState(() => {
    const raw = localStorage.getItem(dismissKey);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  });

  const dismissedRecently =
    dismissedAt > 0 && Date.now() - dismissedAt < RESURFACE_MS;

  const handleDismiss = () => {
    const ts = Date.now();
    setDismissedAt(ts);
    localStorage.setItem(dismissKey, String(ts));
  };

  useEffect(() => {
    if (!dismissedAt) return;
    const now = Date.now();
    const remaining = RESURFACE_MS - (now - dismissedAt);
    if (remaining <= 0) {
      localStorage.removeItem(dismissKey);
      setDismissedAt(0);
      return;
    }
    const timer = setTimeout(() => {
      localStorage.removeItem(dismissKey);
      setDismissedAt(0);
    }, remaining);
    return () => clearTimeout(timer);
  }, [dismissedAt, dismissKey]);

  const onStateChange = useCallback(
    (next) => {
      setState((prev) => ({ ...prev, ...next }));
      if (hideWhenSubscribed && next?.subscribed === true) {
        setForceHidden(true);
      }
    },
    [hideWhenSubscribed],
  );

  // Reconcile subscription/permission on mount so we never show the card if already enabled.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const ok =
        typeof window !== 'undefined' &&
        window.isSecureContext &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        typeof Notification !== 'undefined';
      if (!mounted) return;
      if (!ok) {
        setState((prev) => ({ ...prev, supported: false, ready: false }));
        setHydrated(true);
        return;
      }
      const permission = Notification.permission;
      let subscribed = false;
      let ready = false;
      try {
        const reg =
          (await navigator.serviceWorker.ready) ||
          (await navigator.serviceWorker.getRegistration());
        if (reg) {
          ready = true;
          const sub = await reg.pushManager.getSubscription();
          subscribed = !!sub;
        }
      } catch (_) {
        // ignore
      }
      if (!mounted) return;
      setState((prev) => ({
        ...prev,
        supported: ok,
        permission,
        ready,
        subscribed,
      }));
      if (hideWhenSubscribed && subscribed) setForceHidden(true);
      setHydrated(true);
    })();
    return () => {
      mounted = false;
    };
  }, [hideWhenSubscribed]);

  // Hide immediately when subscription flips to true (covers any path)
  useEffect(() => {
    if (hideWhenSubscribed && state.subscribed) {
      setForceHidden(true);
    }
  }, [hideWhenSubscribed, state.subscribed]);

  const statusHint = useMemo(() => {
    if (!player?.token) return 'Sign in to enable.';
    if (!state.supported) return 'Push is not supported in this browser.';
    if (!process.env.REACT_APP_VAPID_PUBLIC_KEY)
      return 'Missing web push key.';
    if (state.permission === 'denied')
      return 'Notifications are blocked in browser settings.';
    if (!state.ready && !state.subscribed) return 'Service worker not ready.';
    return '';
  }, [player?.token, state]);

  const shouldShowFull =
    hydrated &&
    !dismissedRecently &&
    state.supported &&
    state.permission !== 'denied' &&
    !state.subscribed;

  if (forceHidden && hideWhenSubscribed) {
    return null;
  }

  if (state.subscribed) {
    return hideWhenSubscribed ? null : (
      <div className="feedHighlightCard feedHighlightCard--chip">
        <span className="feedHighlightChipLabel">Live Pings</span>
        <span className="feedHighlightChipValue">On</span>
      </div>
    );
  }

  if (dismissedRecently && !state.subscribed && state.supported) {
    return (
      <div className="feedHighlightCard feedHighlightCard--bell">
        <span className="feedHighlightBell">Bell</span>
        <span className="feedHighlightMeta">Live Pings</span>
      </div>
    );
  }

  if (!shouldShowFull) {
    if (
      process.env.NODE_ENV === 'development' &&
      hydrated &&
      state.subscribed &&
      state.supported &&
      state.permission === 'granted'
    ) {
      // eslint-disable-next-line no-console
      console.error('[push][invariant] card suppressed but subscribed=true');
    }
    return null;
  }

  return (
    <>
      <div
        className="feedHighlightCard feedHighlightCard--push"
        role="button"
        tabIndex={0}
        onClick={(event) => {
          if (event.target.closest('button')) return;
          actionRef.current?.click();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            actionRef.current?.click();
          }
        }}
      >
        <div className="feedHighlightTitleRow">
          <span className="feedHighlightTitle">Enable Live Pings</span>
          <button
            type="button"
            className="feedHighlightInfo"
            title={presenceTooltipCopy}
            onClick={(event) => {
              event.stopPropagation();
              setShowPresenceSettings(true);
            }}
            aria-label="Presence info"
          >
            i
          </button>
        </div>
        <span className="feedHighlightMeta">
          Real-time likes, comments, and messages.
        </span>
        {statusHint && <span className="feedHighlightHint">{statusHint}</span>}
        <div className="feedHighlightActionsRow">
          <PushSubscribeButton
            jwt={player?.token}
            vapidKey={process.env.REACT_APP_VAPID_PUBLIC_KEY}
            product={product}
            onStateChange={onStateChange}
            onSubscribed={() => {
              setState((prev) => ({ ...prev, subscribed: true, permission: 'granted' }));
              if (hideWhenSubscribed) setForceHidden(true);
            }}
            variant="compact"
            className="feedHighlightAction"
            buttonRef={actionRef}
          />
          <button
            type="button"
            className="feedHighlightDismiss"
            onClick={handleDismiss}
          >
            Not now
          </button>
        </div>
      </div>
      <PresenceSettingsModal
        open={showPresenceSettings}
        onClose={() => setShowPresenceSettings(false)}
      />
    </>
  );
}
